import { useEffect, useRef, useState } from "react"
import { Mail, Phone, MessageCircle, Clock, Send, Check, TriangleAlert, LoaderCircle, type LucideIcon } from "lucide-react"
import { Container } from "@/components/layout/Container"
import { Eyebrow } from "@/components/shared/Eyebrow"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { CONTACT, telHref, whatsappHref, mailtoHref } from "@/content/contact"
import {
	contactFormFieldsSchema,
	submitContactForm,
	MAX_COMMENTS,
	CONTACT_COOLDOWN_MS,
	CONTACT_SENT_KEY
} from "@/features/contact/contactForm"

interface TreatmentContactProps {
	/** Id del tratamiento (de la URL); se envía en el payload del webhook. */
	treatmentId: string
}

type Status = "idle" | "submitting" | "success" | "error"
type FieldErrors = Partial<Record<"name" | "phone" | "comments", string>>

interface ContactChannel {
	icon: LucideIcon
	label: string
	value: string
	href: string
}

const CHANNELS: ContactChannel[] = [
	{ icon: MessageCircle, label: "WhatsApp", value: CONTACT.whatsapp, href: whatsappHref() },
	{ icon: Phone, label: "Teléfono", value: CONTACT.phone, href: telHref },
	{ icon: Mail, label: "Email", value: CONTACT.email, href: mailtoHref() }
]

/**
 * Sección 7 (contacto),validado con Zod que envía a un webhook de
 * make.com. Muestra estado de éxito o error en lugar de los campos y, tras enviar,
 * espera 5 min (persistidos en localStorage) antes de volver a mostrarse.
 */
export function TreatmentContact({ treatmentId }: TreatmentContactProps): React.JSX.Element {
	const [name, setName] = useState("")
	const [phone, setPhone] = useState("")
	const [comments, setComments] = useState("")
	const [errors, setErrors] = useState<FieldErrors>({})
	const [status, setStatus] = useState<Status>("idle")
	const resetTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

	function clearForm(): void {
		setName("")
		setPhone("")
		setComments("")
		setErrors({})
	}

	function scheduleReset(sentAt: number): void {
		const remaining = CONTACT_COOLDOWN_MS - (Date.now() - sentAt)
		clearTimeout(resetTimer.current)
		if (remaining <= 0) {
			clearForm()
			setStatus("idle")
			return
		}
		resetTimer.current = setTimeout(() => {
			clearForm()
			setStatus("idle")
		}, remaining)
	}

	// Al montar, respeta el cooldown persistido: si el último envío fue hace menos
	// de 5 min, mantiene el estado de éxito hasta que expire.
	useEffect(() => {
		const raw = localStorage.getItem(CONTACT_SENT_KEY)
		const sentAt = raw ? Number(raw) : 0
		if (!sentAt || Date.now() - sentAt >= CONTACT_COOLDOWN_MS) {
			return () => clearTimeout(resetTimer.current)
		}
		setStatus("success")
		const remaining = CONTACT_COOLDOWN_MS - (Date.now() - sentAt)
		clearTimeout(resetTimer.current)
		if (remaining <= 0) {
			setName("")
			setPhone("")
			setComments("")
			setErrors({})
			setStatus("idle")
		} else {
			resetTimer.current = setTimeout(() => {
				setName("")
				setPhone("")
				setComments("")
				setErrors({})
				setStatus("idle")
			}, remaining)
		}
		return () => clearTimeout(resetTimer.current)
	}, [])

	async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault()
		const result = contactFormFieldsSchema.safeParse({ name, phone, comments })
		if (!result.success) {
			const fieldErrors = result.error.flatten().fieldErrors
			setErrors({ name: fieldErrors.name?.[0], phone: fieldErrors.phone?.[0], comments: fieldErrors.comments?.[0] })
			return
		}
		setErrors({})
		setStatus("submitting")
		// El spinner dura al menos 2 s aunque el webhook responda antes.
		const minSpin = new Promise(resolve => setTimeout(resolve, 2000))
		try {
			await Promise.all([submitContactForm(result.data, treatmentId), minSpin])
			const sentAt = Date.now()
			localStorage.setItem(CONTACT_SENT_KEY, String(sentAt))
			clearForm()
			setStatus("success")
			scheduleReset(sentAt)
		} catch {
			await minSpin
			setStatus("error")
		}
	}

	function clearError(field: keyof FieldErrors): void {
		setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev))
	}

	return (
		<section className="bg-background">
			<Container className="py-16 md:py-24">
				<Eyebrow align="center">Contacto</Eyebrow>
				<div className="mt-8 grid gap-10 lg:grid-cols-2 lg:gap-16">
					{/* Columna izquierda: formulario o resultado */}
					<div>
						{status === "success" ? (
							<ResultMessage
								tone="success"
								icon={Check}
								title="¡Mensaje enviado!"
								message="Gracias por escribirnos. Te contactaremos lo antes posible."
							/>
						) : status === "error" ? (
							<ResultMessage
								tone="error"
								icon={TriangleAlert}
								title="No pudimos enviar tu mensaje"
								message="Ocurrió un problema al enviar. Por favor, inténtalo de nuevo."
							>
								<Button size="lg" className="mt-6" onClick={() => setStatus("idle")}>
									Intentar de nuevo
								</Button>
							</ResultMessage>
						) : (
							<>
								<h2 className="text-balance font-heading text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl">
									¿Tienes preguntas? Escríbenos
								</h2>
								<p className="mt-4 text-pretty text-muted-foreground">
									Déjanos tus datos y un mensaje; te responderemos lo antes posible.
								</p>
								<form className="mt-8 space-y-4" onSubmit={event => void handleSubmit(event)} noValidate>
									<div className="space-y-2">
										<Label htmlFor="contact-name">Nombre completo</Label>
										<Input
											id="contact-name"
											value={name}
											onChange={e => {
												setName(e.target.value)
												clearError("name")
											}}
											placeholder="Nombre y apellido"
											autoComplete="name"
											disabled={status === "submitting"}
											aria-invalid={errors.name ? true : undefined}
										/>
										{errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
									</div>
									<div className="space-y-2">
										<Label htmlFor="contact-phone">Teléfono</Label>
										<Input
											id="contact-phone"
											type="tel"
											value={phone}
											onChange={e => {
												setPhone(e.target.value)
												clearError("phone")
											}}
											placeholder="55 1234 5678"
											autoComplete="tel"
											disabled={status === "submitting"}
											aria-invalid={errors.phone ? true : undefined}
										/>
										{errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
									</div>
									<div className="space-y-2">
										<Label htmlFor="contact-comments">Comentarios</Label>
										<textarea
											id="contact-comments"
											value={comments}
											onChange={e => {
												setComments(e.target.value)
												clearError("comments")
											}}
											placeholder="¿En qué podemos ayudarte?"
											rows={4}
											maxLength={MAX_COMMENTS}
											disabled={status === "submitting"}
											aria-invalid={errors.comments ? true : undefined}
											className={cn(
												"w-full min-w-0 rounded-2xl border border-input bg-input/30 px-3 py-2 text-base transition-colors outline-none",
												"placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 md:text-sm",
												"aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20"
											)}
										/>
										<div className="flex items-start justify-between gap-3">
											<p className="text-sm text-destructive">{errors.comments}</p>
											<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
												{comments.length}/{MAX_COMMENTS}
											</span>
										</div>
									</div>
									<Button
										type="submit"
										size="lg"
										disabled={status === "submitting"}
										className="w-full sm:w-auto"
									>
										{status === "submitting" ? (
											<LoaderCircle className="animate-spin" aria-label="Enviando" />
										) : (
											<>
												<Send />
												Enviar mensaje
											</>
										)}
									</Button>
								</form>
							</>
						)}
					</div>

					{/* Columna derecha: datos de contacto */}
					<div className="rounded-3xl bg-linear-to-br from-sky-50 to-muted p-8 ring-1 ring-foreground/10 md:p-10">
						<h3 className="font-heading text-xl font-semibold">Contáctanos directo</h3>
						<p className="mt-2 text-sm text-muted-foreground">Elige el medio que prefieras.</p>
						<ul className="mt-8 space-y-5">
							{CHANNELS.map(({ icon: Icon, label, value, href }) => (
								<li key={label}>
									<a
										href={href}
										target="_blank"
										rel="noopener noreferrer"
										className="group flex items-center gap-4 rounded-2xl p-2 transition-colors hover:bg-background/60"
									>
										<span className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
											<Icon className="size-5" />
										</span>
										<span>
											<span className="block text-sm text-muted-foreground">{label}</span>
											<span className="block font-medium group-hover:text-primary">{value}</span>
										</span>
									</a>
								</li>
							))}
							<li className="flex items-center gap-4 p-2">
								<span className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
									<Clock className="size-5" />
								</span>
								<span>
									<span className="block text-sm text-muted-foreground">Horario de atención</span>
									<span className="block font-medium">{CONTACT.hours}</span>
								</span>
							</li>
						</ul>
					</div>
				</div>
			</Container>
		</section>
	)
}

interface ResultMessageProps {
	tone: "success" | "error"
	icon: LucideIcon
	title: string
	message: string
	children?: React.ReactNode
}

/** Mensaje de resultado (éxito/error) con el icono en una caja arriba del texto. */
function ResultMessage({ tone, icon: Icon, title, message, children }: ResultMessageProps): React.JSX.Element {
	return (
		<div className="flex h-full min-h-52 flex-col items-center justify-center text-center duration-500 animate-in fade-in">
			<span
				className={cn(
					"inline-flex size-12 items-center justify-center rounded-xl",
					tone === "success" ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"
				)}
			>
				<Icon className="size-6" />
			</span>
			<h2 className="mt-4 text-balance font-heading text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h2>
			<p className="mt-3 max-w-sm text-pretty text-muted-foreground">{message}</p>
			{children}
		</div>
	)
}
